import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  clearWorkspaceSession,
  readTokenMap,
  readWorkspaces,
  storeWorkspaceSession,
  tokenForWorkspace,
  type WorkspaceSession,
  type WorkspaceSummary,
} from "./workspaces";

interface AuthContextValue {
  token: string | null;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  userId: string | null;
  role: string | null;
  activeWorkspace: WorkspaceSummary | null;
  workspaces: WorkspaceSummary[];
  login: (session: WorkspaceSession) => void;
  refresh: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function parseJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  const workspaces = useMemo(() => readWorkspaces(), [revision]);
  const slug = location.pathname.match(/^\/w\/([^/]+)/)?.[1];
  const activeWorkspace = slug
    ? workspaces.find((workspace) => workspace.slug === decodeURIComponent(slug)) ?? null
    : null;
  const token = activeWorkspace ? tokenForWorkspace(activeWorkspace.id) : null;
  const fallbackToken = Object.values(readTokenMap())[0] ?? null;
  const payload = parseJwtPayload(token ?? fallbackToken ?? "");

  useEffect(() => {
    const sync = () => refresh();
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, [refresh]);

  function login(session: WorkspaceSession) {
    storeWorkspaceSession(session);
    refresh();
  }

  function logout() {
    clearWorkspaceSession();
    refresh();
    window.location.href = "/login";
  }

  return (
    <AuthContext.Provider
      value={{
        token,
        isAuthenticated: Boolean(token),
        isSuperAdmin: Boolean(payload?.superAdmin),
        userId: (payload?.userId as string) ?? null,
        role: (payload?.role as string) ?? null,
        activeWorkspace,
        workspaces,
        login,
        refresh,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function RequireWorkspace({ children }: { children: ReactNode }) {
  const { token, activeWorkspace } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!activeWorkspace || !token) navigate("/workspaces", { replace: true });
  }, [activeWorkspace, token, navigate]);

  if (!activeWorkspace || !token) return null;
  return <>{children}</>;
}

export function RequireAnyAuth({ children }: { children: ReactNode }) {
  const { refresh } = useAuth();
  const authenticated = Object.keys(readTokenMap()).length > 0;
  const navigate = useNavigate();

  useEffect(() => {
    if (!authenticated) {
      clearWorkspaceSession();
      refresh();
    }
  }, [authenticated, refresh]);

  useEffect(() => {
    if (!authenticated) navigate("/login", { replace: true });
  }, [authenticated, navigate]);

  return authenticated ? <>{children}</> : null;
}
